import { useMemo, useState } from 'react';
import { LoadingState, ErrorState, EmptyState, SearchIcon } from '../../components';
import { api, useApi, useMutation } from '../../api';
import type { UserView } from '../../api';
import { UserRole, UserStatus, ALL_ROLES } from '@jalupro/shared';
import type { InviteUserDto } from '@jalupro/shared';
import { useOffices } from '../../offices';
import { useT, useRoleLabel, useRoleDescription, type TFunc } from '../../i18n';
import dict from './Users.i18n';

/**
 * Admin · Users — staff & role management. Route /admin/users (SUPER_ADMIN only,
 * route-gated). Variant-B clean design, structured like Agents.tsx but for the
 * NON-AGENT staff roles: the directory lists super-admins, admins and accountants
 * (agents have their own directory at /admin/agents — the list is fetched with
 * excludeAgents). The drawer lets a super-admin set any staff role.
 *
 * The layout supplies <CleanSidebar>; this screen renders the content to its right:
 * a topbar (totals + search + invite), the directory table (status pills, role pills,
 * last-login), and a right drawer with profile, role selector, status (block/unblock)
 * and account actions (reset password / reset 2FA).
 *
 * Wired to the live /users API:
 *   - GET   /users?search&page&pageSize → api.users.list      (all roles)
 *   - POST  /users/invite               → api.users.invite
 *   - PATCH /users/:id                  → api.users.update     (role)
 *   - PATCH /users/:id/status           → api.users.setStatus  (block / unblock)
 *   - POST  /users/:id/password         → api.users.resetPassword
 *   - POST  /users/:id/reset-2fa        → api.users.resetTwoFactor
 */

/** Role pill tint (clean palette). */
const ROLE_TONE: Record<UserRole, string> = {
  SUPER_ADMIN: 'blue',
  ACCOUNTANT: 'amber',
  BUSINESS_MANAGER: 'blue',
  SALES_MANAGER: 'blue',
  PARTNER: 'muted',
  ASSISTANT: '',
  WAREHOUSE: '',
  AGENT: '',
};

/**
 * Roles invitable from the Users screen. Agents are NOT created here — they are
 * invited from the Agents screen — so AGENT is intentionally excluded.
 */
const INVITABLE_ROLES: readonly UserRole[] = ALL_ROLES.filter((r) => r !== UserRole.AGENT);

const STATUS_TONE: Record<UserStatus, 'green' | 'red' | 'amber'> = {
  ACTIVE: 'green',
  BLOCKED: 'red',
  INVITED: 'amber',
};
const STATUS_AVATAR_BG: Record<UserStatus, string> = {
  ACTIVE: 'var(--line)',
  BLOCKED: 'var(--red-line)',
  INVITED: 'var(--amber-line)',
};

const PAGE_SIZE = 50;

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function sinceLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

/** Short "last login" label (e.g. "31 May 2026") or the translated "never". */
function lastLoginLabel(iso: string | null, t: TFunc): string {
  if (!iso) return t('never');
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export default function AdminUsers() {
  const t = useT(dict);
  const [search, setSearch] = useState('');
  // Drawer opens only on row click / Edit — never auto-opens the first user.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const { data, loading, error, reload } = useApi(
    () =>
      api.users.list({
        search: search.trim() || undefined,
        page: 1,
        pageSize: PAGE_SIZE,
        // Staff screen manages non-agent roles; agents have their own directory.
        excludeAgents: true,
      }),
    [search],
  );

  const rows = data?.items ?? [];
  const counts = useMemo(() => {
    const c = { total: rows.length, active: 0, blocked: 0, invited: 0 };
    for (const r of rows) {
      if (r.status === UserStatus.ACTIVE) c.active += 1;
      else if (r.status === UserStatus.BLOCKED) c.blocked += 1;
      else if (r.status === UserStatus.INVITED) c.invited += 1;
    }
    return c;
  }, [rows]);

  const selected = useMemo<UserView | null>(() => {
    if (selectedId === null) return null;
    return rows.find((r) => r.id === selectedId) ?? null;
  }, [rows, selectedId]);

  const statusLabel: Record<UserStatus, string> = {
    ACTIVE: t('status_active'),
    BLOCKED: t('status_blocked'),
    INVITED: t('status_invited'),
  };

  return (
    <div className="cl row full m-scroll m-stack" style={{ minWidth: 0 }}>
      <div className="col grow m-scroll" style={{ background: 'var(--paper)', overflow: 'hidden', minWidth: 0 }}>
        {/* Topbar */}
        <div
          className="row center between"
          style={{ padding: '14px 22px', borderBottom: '1px solid var(--line)', background: '#fff' }}
        >
          <div className="col">
            <div className="meta">
              {t('summary', {
                total: counts.total,
                active: counts.active,
                blocked: counts.blocked,
                invited: counts.invited,
              })}
            </div>
            <div className="h1">{t('heading')}</div>
          </div>
          <div className="row gap-8 center">
            <div className="input" style={{ width: 220 }}>
              <SearchIcon />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('search_placeholder')}
                aria-label={t('search_placeholder')}
                style={{
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  font: 'inherit',
                  color: 'inherit',
                  flex: 1,
                  minWidth: 0,
                }}
              />
            </div>
            <button type="button" className="btn primary" onClick={() => setInviteOpen(true)}>
              {t('btn_invite')}
            </button>
          </div>
        </div>

        {/* Table + drawer */}
        <div className="row grow m-stack" style={{ overflow: 'hidden', minWidth: 0 }}>
          <div className="col grow" style={{ overflow: 'auto', padding: '14px 22px', minWidth: 0 }}>
            {loading ? (
              <LoadingState label={t('loading_users')} />
            ) : error ? (
              <ErrorState message={error.message} onRetry={reload} />
            ) : rows.length === 0 ? (
              <EmptyState title={t('empty_title')} hint={t('empty_hint')} />
            ) : (
              <div className="panel" style={{ overflow: 'hidden' }}>
                <table>
                  <thead>
                    <tr>
                      <th />
                      <th>{t('th_user')}</th>
                      <th>{t('th_role')}</th>
                      <th>{t('th_status')}</th>
                      <th>{t('th_2fa')}</th>
                      <th>{t('th_joined')}</th>
                      <th>{t('th_last_login')}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const isSel = selected?.id === r.id;
                      return (
                        <tr
                          key={r.id}
                          onClick={() => setSelectedId(r.id)}
                          style={{
                            background: isSel ? 'var(--card-2)' : 'transparent',
                            cursor: 'pointer',
                          }}
                        >
                          <td>
                            <div
                              style={{
                                width: 30,
                                height: 30,
                                borderRadius: '50%',
                                background: STATUS_AVATAR_BG[r.status],
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              {initials(r.fullName)}
                            </div>
                          </td>
                          <td>
                            <div style={{ fontSize: 14, fontWeight: 500 }}>{r.fullName}</div>
                            <div className="meta mono" style={{ fontSize: 12 }}>
                              {r.email}
                            </div>
                          </td>
                          <td>
                            <RolePill role={r.role} />
                          </td>
                          <td>
                            <span className={`pill ${STATUS_TONE[r.status]} dot`}>
                              {statusLabel[r.status]}
                            </span>
                          </td>
                          <td className="meta">{r.totpEnabled ? '✓' : '—'}</td>
                          <td className="meta">{sinceLabel(r.createdAt)}</td>
                          <td className="meta">{lastLoginLabel(r.lastLoginAt, t)}</td>
                          <td style={{ textAlign: 'right' }}>
                            <button
                              type="button"
                              className="btn sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedId(r.id);
                              }}
                            >
                              {t('btn_edit')}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {selected && (
            <UserDrawer
              key={selected.id}
              user={selected}
              onClose={() => setSelectedId(null)}
              onChanged={reload}
            />
          )}
        </div>
      </div>

      {inviteOpen && (
        <InviteDrawer
          onClose={() => setInviteOpen(false)}
          onInvited={() => {
            setInviteOpen(false);
            reload();
          }}
        />
      )}
    </div>
  );
}

/** Localized role pill — uses useRoleLabel so role names stay consistent across the app. */
function RolePill({ role }: { role: UserRole }) {
  const roleLabel = useRoleLabel();
  return <span className={`pill ${ROLE_TONE[role]}`}>{roleLabel(role)}</span>;
}

// ── Profile / role / account-actions drawer ─────────────────────────────────
function UserDrawer({
  user,
  onClose,
  onChanged,
}: {
  user: UserView;
  onClose: () => void;
  onChanged: () => void;
}) {
  const t = useT(dict);
  const roleLabel = useRoleLabel();
  const roleDescription = useRoleDescription();
  const { officeName } = useOffices();

  const update = useMutation(api.users.update);
  const setStatus = useMutation(api.users.setStatus);
  const resetPassword = useMutation(api.users.resetPassword);
  const resetTwoFactor = useMutation(api.users.resetTwoFactor);
  const remove = useMutation(api.users.remove);

  const [roleVal, setRoleVal] = useState<UserRole>(user.role);
  const [notice, setNotice] = useState<string | null>(null);
  // Editable name (split first/last; fullName is the canonical display name).
  const [firstName, setFirstName] = useState(user.firstName ?? user.fullName.split(' ')[0] ?? '');
  const [lastName, setLastName] = useState(user.lastName ?? user.fullName.split(' ').slice(1).join(' '));
  const nextFullName = [firstName, lastName].map((v) => v.trim()).filter(Boolean).join(' ');
  const nameDirty = nextFullName !== user.fullName && nextFullName.length > 0;

  const blocked = user.status === UserStatus.BLOCKED;
  const isAgent = user.role === UserRole.AGENT;
  // Deletion is offered for admins & accountants (agents have their own screen).
  const canDelete = user.role !== UserRole.SUPER_ADMIN && user.role !== UserRole.AGENT;
  const busy =
    update.loading ||
    setStatus.loading ||
    resetPassword.loading ||
    resetTwoFactor.loading ||
    remove.loading;
  const actionError =
    update.error ?? setStatus.error ?? resetPassword.error ?? resetTwoFactor.error ?? remove.error;

  async function saveName() {
    if (!nameDirty) return;
    setNotice(null);
    try {
      await update.mutate(user.id, { firstName: firstName.trim(), lastName: lastName.trim(), fullName: nextFullName });
      setNotice(t('name_saved'));
      onChanged();
    } catch {
      /* actionError surfaced below */
    }
  }

  async function changeRole(next: UserRole) {
    const prev = roleVal;
    setRoleVal(next);
    try {
      await update.mutate(user.id, { role: next });
      onChanged();
    } catch {
      setRoleVal(prev);
    }
  }

  async function toggleBlock() {
    try {
      await setStatus.mutate(user.id, { status: blocked ? UserStatus.ACTIVE : UserStatus.BLOCKED });
      onChanged();
    } catch {
      /* surfaced below */
    }
  }

  async function doResetPassword() {
    setNotice(null);
    const next = window.prompt(t('prompt_set_password'));
    if (!next) return;
    try {
      await resetPassword.mutate(user.id, next);
      setNotice(t('notice_password_reset'));
      onChanged();
    } catch {
      /* surfaced below */
    }
  }

  async function doResetTwoFactor() {
    setNotice(null);
    if (!window.confirm(t('confirm_reset_2fa'))) return;
    try {
      await resetTwoFactor.mutate(user.id);
      setNotice(t('notice_2fa_reset'));
      onChanged();
    } catch {
      /* surfaced below */
    }
  }

  async function doDelete() {
    setNotice(null);
    if (!window.confirm(t('confirm_delete', { name: user.fullName }))) return;
    try {
      await remove.mutate(user.id);
      onChanged();
      onClose();
    } catch {
      /* surfaced below */
    }
  }

  return (
    <div
      style={{
        width: 340,
        background: '#fff',
        borderLeft: '1px solid var(--line)',
        overflow: 'auto',
        flexShrink: 0,
      }}
    >
      <div className="row between center" style={{ padding: '14px 18px', borderBottom: '1px solid var(--line-2)' }}>
        <div className="h2">{user.fullName}</div>
        <button
          type="button"
          onClick={onClose}
          className="meta"
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14 }}
          aria-label={t('drawer_close')}
        >
          ✕
        </button>
      </div>

      <div className="col" style={{ padding: '14px 18px' }}>
        <div className="row center gap-12 mb-12">
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: STATUS_AVATAR_BG[user.status],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
            }}
          >
            {initials(user.fullName)}
          </div>
          <div className="col">
            <div style={{ fontSize: 14, fontWeight: 600 }}>{user.email}</div>
            <div className="meta">
              {(user.office ? officeName(user.office) : '—') + ' · ' + roleLabel(user.role) + ' ' + t('drawer_since') + ' '}
              {sinceLabel(user.createdAt)}
            </div>
          </div>
        </div>

        <div className="h3 mb-8">{t('section_profile')}</div>
        <div className="col gap-8" style={{ marginBottom: 8 }}>
          <label className="col gap-4">
            <span className="label">{t('first_name')}</span>
            <input className="input" style={{ width: '100%' }} value={firstName} onChange={(e) => setFirstName(e.target.value)} disabled={busy} />
          </label>
          <label className="col gap-4">
            <span className="label">{t('last_name')}</span>
            <input className="input" style={{ width: '100%' }} value={lastName} onChange={(e) => setLastName(e.target.value)} disabled={busy} />
          </label>
        </div>
        <div className="row between center" style={{ marginBottom: 14 }}>
          <span className="meta" style={{ fontSize: 12 }}>{nextFullName || '—'}</span>
          <button type="button" className="btn sm primary" disabled={busy || !nameDirty} onClick={saveName}>
            {update.loading ? t('btn_saving_name') : t('btn_save_name')}
          </button>
        </div>

        <div className="divider" style={{ margin: '0 0 14px' }} />

        <div className="h3 mb-8">{t('section_role')}</div>
        <select
          value={roleVal}
          disabled={busy || isAgent}
          onChange={(e) => changeRole(e.target.value as UserRole)}
          className="input"
          style={{ width: '100%', marginBottom: 6 }}
        >
          {(Object.keys(UserRole) as UserRole[]).map((r) => (
            <option key={r} value={r}>
              {roleLabel(r)}
            </option>
          ))}
        </select>
        <div className="meta" style={{ fontSize: 12, marginBottom: 14 }}>
          {isAgent ? t('role_agent_locked') : roleDescription(roleVal)}
        </div>

        <div className="divider" style={{ margin: '14px 0' }} />

        <div className="h3 mb-8">{t('section_account')}</div>
        <div className="col gap-6">
          <button
            type="button"
            className="btn"
            style={{ justifyContent: 'flex-start' }}
            disabled={busy}
            onClick={doResetPassword}
          >
            {resetPassword.loading ? t('btn_resetting_password') : t('btn_reset_password')}
          </button>
          <button
            type="button"
            className="btn"
            style={{ justifyContent: 'flex-start' }}
            disabled={busy || !user.totpEnabled}
            title={user.totpEnabled ? undefined : t('tooltip_no_2fa')}
            onClick={doResetTwoFactor}
          >
            {resetTwoFactor.loading ? t('btn_resetting_2fa') : t('btn_reset_2fa')}
          </button>
        </div>

        <div className="divider" style={{ margin: '14px 0' }} />

        <div className="col gap-6">
          <button
            type="button"
            className={blocked ? 'btn' : 'btn red'}
            style={{ justifyContent: 'center' }}
            disabled={busy}
            onClick={toggleBlock}
          >
            {setStatus.loading
              ? blocked
                ? t('btn_unblocking')
                : t('btn_blocking')
              : blocked
                ? t('btn_unblock')
                : t('btn_block')}
          </button>
          {canDelete && (
            <button
              type="button"
              className="btn"
              style={{ justifyContent: 'center', color: 'var(--red)', borderColor: 'var(--red)' }}
              disabled={busy}
              onClick={doDelete}
            >
              {remove.loading ? t('btn_deleting') : t('btn_delete')}
            </button>
          )}
          <div className="meta" style={{ fontSize: 12 }}>
            {t('helper_block')}
          </div>
          {notice && (
            <div className="meta" style={{ color: 'var(--green)', fontSize: 13 }}>
              {notice}
            </div>
          )}
          {actionError && <ErrorState message={actionError.message} />}
        </div>
      </div>
    </div>
  );
}

// ── Invite-user drawer ──────────────────────────────────────────────────────
function InviteDrawer({ onClose, onInvited }: { onClose: () => void; onInvited: () => void }) {
  const t = useT(dict);
  const roleLabel = useRoleLabel();
  const roleDescription = useRoleDescription();

  const invite = useMutation(api.users.invite);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>(UserRole.SALES_MANAGER);
  /** Set after creation — the one-time temporary password to show the admin. */
  const [created, setCreated] = useState<{ email: string; temporaryPassword: string } | null>(null);

  const canSubmit = fullName.trim().length > 0 && email.trim().length > 0 && !invite.loading;

  async function submit() {
    const dto: InviteUserDto = {
      fullName: fullName.trim(),
      email: email.trim(),
      role,
    };
    try {
      const res = await invite.mutate(dto);
      setCreated({ email: res.email, temporaryPassword: res.temporaryPassword });
    } catch {
      /* surfaced below */
    }
  }

  const fieldStyle = { width: '100%' } as const;

  return (
    <div
      style={{
        width: 340,
        background: '#fff',
        borderLeft: '1px solid var(--line)',
        overflow: 'auto',
        flexShrink: 0,
      }}
    >
      <div className="row between center" style={{ padding: '14px 18px', borderBottom: '1px solid var(--line-2)' }}>
        <div className="h2">{t('invite_heading')}</div>
        <button
          type="button"
          onClick={onClose}
          className="meta"
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14 }}
          aria-label={t('invite_close')}
        >
          ✕
        </button>
      </div>

      {created ? (
        <div className="col gap-12" style={{ padding: '14px 18px' }}>
          <div className="h3">{t('invite_created_heading')}</div>
          <div className="meta" style={{ fontSize: 13 }}>
            {t('invite_created_hint', { email: created.email })}
          </div>
          <label className="col gap-4">
            <span className="h3">{t('invite_temp_password')}</span>
            <input
              className="input mono"
              style={fieldStyle}
              readOnly
              value={created.temporaryPassword}
              onFocus={(e) => e.currentTarget.select()}
            />
          </label>
          <div className="meta" style={{ fontSize: 12 }}>
            {t('invite_temp_password_note')}
          </div>
          <button
            type="button"
            className="btn primary"
            style={{ justifyContent: 'center' }}
            onClick={onInvited}
          >
            {t('invite_done')}
          </button>
        </div>
      ) : (
        <div className="col gap-12" style={{ padding: '14px 18px' }}>
          <label className="col gap-4">
            <span className="h3">{t('invite_label_fullname')}</span>
            <input
              className="input"
              style={fieldStyle}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="María López"
            />
          </label>
          <label className="col gap-4">
            <span className="h3">{t('invite_label_email')}</span>
            <input
              className="input"
              type="email"
              style={fieldStyle}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@jalupro.es"
            />
          </label>
          <label className="col gap-4">
            <span className="h3">{t('invite_label_role')}</span>
            <select
              className="input"
              style={fieldStyle}
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
            >
              {INVITABLE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r)}
                </option>
              ))}
            </select>
            <span className="meta" style={{ fontSize: 12 }}>
              {roleDescription(role)}
            </span>
          </label>

          <div className="meta" style={{ fontSize: 12 }}>
            {t('invite_hint')}
          </div>

          {invite.error && <ErrorState message={invite.error.message} />}

          <div className="row gap-6">
            <button type="button" className="btn" onClick={onClose} disabled={invite.loading}>
              {t('btn_cancel')}
            </button>
            <button
              type="button"
              className="btn primary"
              style={{ flex: 1, justifyContent: 'center' }}
              disabled={!canSubmit}
              onClick={submit}
            >
              {invite.loading ? t('btn_sending_invite') : t('btn_send_invite')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
